import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaCalendar, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { getNews } from '../api/news';
import type { News } from '../api/types';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  position: relative;
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/img/cow1.jpg') center/cover no-repeat;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(107, 142, 35, 0.5) 0%, rgba(210, 105, 30, 0.5) 100%);
    z-index: 1;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ContentSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: ${props => props.theme.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.primaryLight};
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  position: relative;
  margin-bottom: -2px;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primaryDark : props.theme.colors.primaryLight};
    color: white;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: 1rem;
  }
`;

const SearchWrapper = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: static;
    width: 100%;
  }
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  padding-left: 50px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 1rem;
  outline: none;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textLight};
  font-size: 1.2rem;
`;

const FeaturedSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const FeaturedSliderContainer = styled.div`
  position: relative;

  .swiper {
    padding: 20px 0 50px;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: ${props => props.theme.colors.primary};
    &:after {
      font-size: 1.5rem;
    }
  }

  .swiper-pagination-bullet {
    background: ${props => props.theme.colors.primary};
  }
`;

const FeaturedCard = styled(motion.div)`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.lg};
  transition: all ${props => props.theme.transitions.normal};
  cursor: pointer;
  height: 450px;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 400px;
  }
`;

const FeaturedImage = styled.div<{ image?: string }>`
  height: 300px;
  background: ${props => props.image
    ? `url(${props.image}) center/cover no-repeat`
    : `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.accent})`};
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 250px;
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  left: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.85rem;
  font-weight: 600;
`;

const FeaturedContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FeaturedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.textLight};
  font-size: 0.9rem;
`;

const FeaturedNewsTitle = styled.h4`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.4;
  font-weight: 600;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const FeaturedExcerpt = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsListSection = styled.div`
  margin-top: ${props => props.theme.spacing.xxl};
`;

const BoardList = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  min-height: 800px;
  display: flex;
  flex-direction: column;
`;

const BoardTable = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TableBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  background: white;
  color: ${props => props.theme.colors.text};
  padding: ${props => props.theme.spacing.md};
  font-weight: 600;
  border-bottom: 2px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 60px 1fr 100px;
  }
`;

const TableHeaderCell = styled.div`
  text-align: center;

  &.title {
    text-align: left;
    padding-left: ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &.hide-mobile {
      display: none;
    }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  transition: background ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.background};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 60px 1fr 100px;
  }
`;

const TableCell = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textLight};

  &.num {
    font-weight: 500;
    color: ${props => props.theme.colors.text};
  }

  &.title {
    justify-content: flex-start;
    text-align: left;
    font-weight: 500;
    color: ${props => props.theme.colors.text};
    padding-left: ${props => props.theme.spacing.md};

    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }

  &.category {
    justify-content: flex-start;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &.hide-mobile {
      display: none;
    }
  }
`;

const PaginationAndSearchContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg} 0;
  min-height: 60px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 40px;
  height: 40px;
  padding: 0 ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.$active ? props.theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.$active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #e74c3c;
  font-size: 1.1rem;
`;

const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(t('news.categories.notice'));
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newsData, setNewsData] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 10;

  // Type cast icons
  const CalendarIcon = FaCalendar as IconType;
  const SearchIconComponent = FaSearch as IconType;
  const ChevronLeftIcon = FaChevronLeft as IconType;
  const ChevronRightIcon = FaChevronRight as IconType;

  // 날짜 포맷팅 함수 (연월일만 표시)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // 2025-10-22T01:43:21.781331 → 2025-10-22
  };

  // 공지사항만 표시
  const categories = [
    t('news.categories.notice')
  ];

  // API에서 뉴스 데이터 가져오기
  useEffect(() => {
    const fetchNewsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getNews({ published: true, lang: i18n.language });
        setNewsData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load news');
        console.error('Failed to fetch news:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsData();
  }, [i18n.language]);

  // Featured 뉴스 (업계동향 제외, 최신순 3개) - 정적
  const featuredNews = newsData
    .filter(news => news.category !== '업계동향')
    .slice(0, 3);

  // 필터링된 뉴스 (카테고리 + 검색)
  const filteredNews = newsData.filter(news => {
    // 공지사항 탭에는 news, notice, press 모두 포함
    const allowedCategories = ['news', 'notice', 'press', '공지사항'];
    const matchesCategory = allowedCategories.includes(news.category);
    const matchesSearch = news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         news.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // 전체 뉴스 (페이지네이션)
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNews = filteredNews.slice(startIndex, startIndex + itemsPerPage);

  // 페이지 번호 생성 로직 (1 2 3 ... 10 스타일)
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      // 7페이지 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 항상 첫 페이지 표시
      pages.push(1);

      if (currentPage <= 3) {
        // 현재 페이지가 앞쪽일 때: 1 2 3 4 ... 10
        pages.push(2, 3, 4);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 현재 페이지가 뒤쪽일 때: 1 ... 7 8 9 10
        pages.push('...');
        pages.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 현재 페이지가 중간일 때: 1 ... 4 5 6 ... 10
        pages.push('...');
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // 페이지 변경 시 currentPage 리셋
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchTerm]);

  return (
    <Container>
      <HeroSection>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t('news.title')}
        </HeroTitle>
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t('news.subtitle')}
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        {loading && (
          <LoadingMessage>{t('news.loading') || 'Loading news...'}</LoadingMessage>
        )}

        {error && (
          <ErrorMessage>
            {t('news.error') || 'Error loading news'}: {error}
          </ErrorMessage>
        )}

        {!loading && !error && (
          <>
            {/* 최신소식 슬라이더 */}
            <FeaturedSection>
                <FeaturedSliderContainer>
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={30}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    breakpoints={{
                      768: {
                        slidesPerView: 2,
                        spaceBetween: 30
                      },
                      1024: {
                        slidesPerView: 3,
                        spaceBetween: 30
                      }
                    }}
                  >
                    {featuredNews.map((news) => (
                      <SwiperSlide key={news.id}>
                        <FeaturedCard
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                          viewport={{ once: true }}
                          onClick={() => navigate(`/news/${news.id}`)}
                        >
                          <FeaturedImage image={news.thumbnailUrl || undefined}>
                            <FeaturedBadge>{news.category}</FeaturedBadge>
                          </FeaturedImage>
                          <FeaturedContent>
                            <FeaturedMeta>
                              <CalendarIcon />
                              {formatDate(news.publishedAt)}
                            </FeaturedMeta>
                            <FeaturedNewsTitle>{news.title}</FeaturedNewsTitle>
                            <FeaturedExcerpt>{news.excerpt}</FeaturedExcerpt>
                          </FeaturedContent>
                        </FeaturedCard>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </FeaturedSliderContainer>
            </FeaturedSection>

        {/* 뉴스 리스트 */}
        <NewsListSection>
          {/* 카테고리 탭 */}
          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab
                key={category}
                $active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <BoardList>
            <BoardTable>
              <TableHeader>
                <TableHeaderCell>번호</TableHeaderCell>
                <TableHeaderCell className="title">제목</TableHeaderCell>
                <TableHeaderCell className="hide-mobile">날짜</TableHeaderCell>
              </TableHeader>

              <TableBody>
                {paginatedNews.length > 0 ? (
                  paginatedNews.map((news, index) => (
                    <TableRow
                      key={news.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      onClick={() => navigate(`/news/${news.id}`)}
                    >
                      <TableCell className="num">
                        {filteredNews.length - (startIndex + index)}
                      </TableCell>
                      <TableCell className="title category">
                        {news.title}
                      </TableCell>
                      <TableCell className="hide-mobile">{formatDate(news.publishedAt)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <EmptyState>{t('news.noResults')}</EmptyState>
                )}
              </TableBody>
            </BoardTable>
          </BoardList>

          {/* 페이지네이션 & 검색창 */}
          <PaginationAndSearchContainer>
            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <PaginationContainer>
                {/* 이전 페이지 */}
                <PageButton
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="이전 페이지"
                >
                  <ChevronLeftIcon />
                </PageButton>

                {/* 페이지 번호들 */}
                {pageNumbers.map((page, index) => {
                  if (page === '...') {
                    return (
                      <PageButton
                        key={`ellipsis-${index}`}
                        disabled
                        style={{ cursor: 'default', opacity: 1 }}
                      >
                        ...
                      </PageButton>
                    );
                  }
                  return (
                    <PageButton
                      key={page}
                      $active={currentPage === page}
                      onClick={() => setCurrentPage(page as number)}
                    >
                      {page}
                    </PageButton>
                  );
                })}

                {/* 다음 페이지 */}
                <PageButton
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  title="다음 페이지"
                >
                  <ChevronRightIcon />
                </PageButton>
              </PaginationContainer>
            )}

            {/* 검색창 */}
            <SearchWrapper>
              <SearchBox>
                <SearchIcon>
                  <SearchIconComponent />
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder={t('news.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchBox>
            </SearchWrapper>
          </PaginationAndSearchContainer>
        </NewsListSection>
          </>
        )}
      </ContentSection>
    </Container>
  );
};

export default NewsPage;